import { prodFirebaseConfig } from "./prodConfig";
import { stgFirebaseConfig } from "./stgConfig";

export type FirebaseEnvironment = "stg" | "prod";
export type AppEnvironment = "development" | "staging" | "production";

type EnvRecord = Record<string, string | undefined>;
type ImportMetaWithEnv = ImportMeta & {
	env?: EnvRecord;
};

const processEnv =
	(globalThis as typeof globalThis & { process?: { env?: EnvRecord } }).process
		?.env ?? {};
const viteEnv = (import.meta as ImportMetaWithEnv).env ?? {};

const viteFirebaseEnvironment =
	viteEnv.VITE_FIREBASE_ENV ?? processEnv.VITE_FIREBASE_ENV;
const firebaseEnvironmentFromProcess = processEnv.FIREBASE_ENV;
const configuredAppEnvironment = viteEnv.VITE_APP_ENV ?? processEnv.APP_ENV;

if (
	viteFirebaseEnvironment &&
	firebaseEnvironmentFromProcess &&
	viteFirebaseEnvironment !== firebaseEnvironmentFromProcess
) {
	throw new Error(
		`Conflicting Firebase environments: VITE_FIREBASE_ENV=${viteFirebaseEnvironment}, FIREBASE_ENV=${firebaseEnvironmentFromProcess}`,
	);
}

const configuredEnvironment =
	viteFirebaseEnvironment ?? firebaseEnvironmentFromProcess;

function normalizeAppEnvironment(
	value: string | undefined,
	firebaseValue: string | undefined,
): AppEnvironment {
	if (value === "prod" || value === "production") {
		return "production";
	}
	if (value === "stg" || value === "staging") {
		return "staging";
	}
	if (value === "dev" || value === "development") {
		return "development";
	}
	if (firebaseValue === "prod") {
		return "production";
	}
	if (firebaseValue === "stg") {
		return "staging";
	}
	return "development";
}

export const firebaseEnvironment: FirebaseEnvironment =
	configuredEnvironment === "prod" ? "prod" : "stg";

export const appEnvironment: AppEnvironment = normalizeAppEnvironment(
	configuredAppEnvironment,
	configuredEnvironment,
);

export const appEnvironmentLabel =
	appEnvironment === "production"
		? "Production"
		: appEnvironment === "staging"
			? "Staging"
			: "Development";

export const firebaseEnvironmentLabel =
	firebaseEnvironment === "prod" ? "Production" : "Staging";

export const firebaseConfig =
	firebaseEnvironment === "prod" ? prodFirebaseConfig : stgFirebaseConfig;

/** Backend credentials must match the project that minted the custom token. */
export function firebaseConfigForProject(projectId: string) {
	if (projectId === prodFirebaseConfig.projectId) return prodFirebaseConfig;
	if (projectId === stgFirebaseConfig.projectId) return stgFirebaseConfig;
	throw new Error(`Unsupported Firebase project: ${projectId}`);
}
